import { type Socket, io } from 'socket.io-client';

import { apiConnector } from '@/api/api.connector';
import {
  PAYMENT_SCHEDULE_UPDATED_SOCKET_EVENT,
  REQUEST_PROGRESS_SOCKET_EVENT,
  REQUEST_SOCKET_DASHBOARD_REFRESH_STEPS
} from '@/shared/constants/request-socket';
import { getClientUuidFromToken } from '@/shared/services/client-uuid';
import { parseRequestSocketProgress } from '@/shared/services/request-socket-event';
import { getUserToken } from '@/shared/services/user-hash';
import { isInWebView } from '@/shared/webview/env';
import { type TAppDispatch, store } from '@/store';
import {
  SCHEDULE_FETCH_LIMIT,
  getDashboardData,
  getPaymentSchedule
} from '@/store/leasing.slice';
import { getRequestDetail, getRequestsList } from '@/store/requests.slice';

let socket: Socket | null = null;

interface ISocketConnectionConfig {
  host: string;
  path: string;
}

const resolveSocketConnection = (): ISocketConnectionConfig | null => {
  const socketUrl =
    process.env.PUBLIC_SOCKET_URL ?? process.env.PUBLIC_API_URL ?? '';

  if (!socketUrl) {
    return null;
  }

  try {
    const parsedUrl = new URL(socketUrl);
    const pathPrefix = parsedUrl.pathname.replace(/\/$/, '');

    return {
      host: `${parsedUrl.protocol}//${parsedUrl.host}`,
      path: pathPrefix ? `${pathPrefix}/socket.io` : '/socket.io'
    };
  } catch {
    return null;
  }
};

const getOpenedRequestUuidFromLocation = (): string | null => {
  const openedRequestUuid = window.location.pathname.match(
    /\/leasing\/requests\/([^/?#]+)/
  )?.[1];

  if (!openedRequestUuid || openedRequestUuid === 'new') {
    return null;
  }

  return openedRequestUuid;
};

const handleRequestProgress = (dispatch: TAppDispatch, rawEvent: unknown) => {
  const progress = parseRequestSocketProgress(rawEvent);

  if (!progress) {
    return;
  }

  const { requestUuid, step } = progress;

  const openedRequestUuid = getOpenedRequestUuidFromLocation();

  if (openedRequestUuid === requestUuid) {
    dispatch(getRequestDetail(requestUuid));
  }

  dispatch(getRequestsList());

  if (REQUEST_SOCKET_DASHBOARD_REFRESH_STEPS.includes(step)) {
    dispatch(getDashboardData());
  }
};

const handlePaymentScheduleUpdated = (dispatch: TAppDispatch) => {
  const loadedContractId =
    store.getState().leasing.paymentSchedule?.data[0]?.contractUuid ?? null;

  if (!loadedContractId) {
    return;
  }

  dispatch(
    getPaymentSchedule({
      contractId: loadedContractId,
      params: { limit: SCHEDULE_FETCH_LIMIT, offset: 0 }
    })
  );
};

const resolveAccessToken = (): string => {
  if (isInWebView() && apiConnector.ssoJwtToken) {
    return apiConnector.ssoJwtToken;
  }

  return getUserToken().accessToken;
};

export const connectLeasingSocket = (dispatch: TAppDispatch) => {
  if (socket?.connected) {
    return;
  }

  const clientUuid = getClientUuidFromToken();
  const socketConnection = resolveSocketConnection();

  if (!clientUuid || !resolveAccessToken() || !socketConnection) {
    return;
  }

  socket = io(socketConnection.host, {
    path: socketConnection.path,
    transports: ['polling'],
    upgrade: false,
    reconnection: true,
    reconnectionDelay: 3000,
    reconnectionDelayMax: 15000,
    auth: (callback) => {
      callback({
        access_token: resolveAccessToken(),
        token: {
          uuid: clientUuid
        }
      });
    }
  });

  socket.on(REQUEST_PROGRESS_SOCKET_EVENT, (rawEvent: unknown) => {
    handleRequestProgress(dispatch, rawEvent);
  });

  socket.on(PAYMENT_SCHEDULE_UPDATED_SOCKET_EVENT, () => {
    handlePaymentScheduleUpdated(dispatch);
  });
};

export const disconnectLeasingSocket = () => {
  if (!socket) {
    return;
  }

  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
};
